import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Divider,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  DragIndicator,
  Home as HomeIcon,
  StarBorder,
  Preview as PreviewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getMaterialIconComponent } from 'app/components/MaterialIconMap';
import { SidebarNavigationItem } from '../../types';
import Role from 'app/apiService/role/roleApiService';
import { useTheme } from '@mui/material';

interface SidebarManagementProps {
  existingItems: SidebarNavigationItem[];
  availableItems: SidebarNavigationItem[];
  onUpdate: (selectedItems: SidebarNavigationItem[], defaultHome: string) => void;
  oldLayout: boolean;
}

interface SortableItemProps {
  item: SidebarNavigationItem;
  isSelected: boolean;
  onToggle: () => void;
  isDefault?: boolean;
  onSetDefault?: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ item, isSelected, onToggle, isDefault, onSetDefault }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.link });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = getMaterialIconComponent(item.icon);

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 0.25,
        py: 0.5,
        borderRadius: 1,
        backgroundColor: isSelected ? 'rgba(255, 165, 0, 0.2)' : isDefault ? 'rgba(0, 0, 0, 0.05)' : 'background.paper',
        border: isDefault ? '2px solid #000' : '1px solid transparent',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: isSelected ? 'rgba(255, 165, 0, 0.3)' : 'action.hover',
        },
      }}
      onMouseDown={(e) => {
        // Only toggle selection if not clicking on drag handle
        const target = e.target as HTMLElement;
        const dragHandle = target.closest('[data-drag-handle]');
        if (!dragHandle) {
          // Don't prevent default on mousedown to allow drag to work
          console.log('Item mousedown, will toggle selection for:', item.link);
        }
      }}
      onClick={(e) => {
        // Only toggle selection if not clicking on drag handle
        const target = e.target as HTMLElement;
        const dragHandle = target.closest('[data-drag-handle]');
        if (!dragHandle) {
          e.preventDefault();
          e.stopPropagation();
          console.log('Item clicked, toggling selection for:', item.link);
          onToggle();
        }
      }}
    >
      <Box
        data-drag-handle
        sx={{ 
          minWidth: 32, 
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1,
        }}
        {...listeners}
        {...attributes}
        onMouseDown={(e) => {
          // Stop propagation to prevent item selection when clicking drag handle
          e.stopPropagation();
        }}
        onClick={(e) => {
          // Stop propagation to prevent item selection when clicking drag handle
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <DragIndicator fontSize="small" color="action" />
      </Box>
      {IconComponent && (
        <ListItemIcon sx={{ minWidth: 28 }}>
          {React.cloneElement(IconComponent, { sx: { fontSize: 18 } })}
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.label}
        secondary={isDefault ? 'Default Home' : item.link}
        primaryTypographyProps={{ 
          fontWeight: isSelected || isDefault ? 'bold' : 'normal',
          fontSize: '0.875rem',
        }}
        secondaryTypographyProps={{
          fontSize: '0.75rem',
        }}
        sx={{ my: 0 }}
      />
      {isDefault && (
        <HomeIcon sx={{ color: 'primary.main', ml: 1 }} fontSize="small" />
      )}
      {onSetDefault && (
        <Tooltip title="Make it default home">
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSetDefault();
            }}
            sx={{
              ml: 1,
              color: isDefault ? 'primary.main' : 'action.active',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <StarBorder fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
};

const SidebarManagement: React.FC<SidebarManagementProps> = ({
  existingItems: initialExistingItems,
  availableItems: initialAvailableItems,
  onUpdate,
  oldLayout,
}) => {
  // Helper function to remove duplicates based on link
  const deduplicateItems = (items: SidebarNavigationItem[]): SidebarNavigationItem[] => {
    const seen = new Set<string>();
    return items.filter(item => {
      if (seen.has(item.link)) {
        return false;
      }
      seen.add(item.link);
      return true;
    });
  };

  // Helper function to assign order to items based on their position
  const assignOrder = (items: SidebarNavigationItem[]): SidebarNavigationItem[] => {
    return items.map((item, index) => ({
      ...item,
      order: item.order !== undefined ? item.order : index + 1,
    }));
  };

  // Helper function to update order after reordering
  const updateOrder = (items: SidebarNavigationItem[]): SidebarNavigationItem[] => {
    return items.map((item, index) => ({
      ...item,
      order: index + 1,
    }));
  };

  const [existingItems, setExistingItems] = useState<SidebarNavigationItem[]>(() => {
    const items = deduplicateItems(initialExistingItems || []);
    // If items already have order, sort by it, otherwise assign order
    const sorted = items.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return 0;
    });
    return assignOrder(sorted);
  });
  const [availableItems, setAvailableItems] = useState<SidebarNavigationItem[]>(() => 
    deduplicateItems(initialAvailableItems || [])
  );
  const [selectedExisting, setSelectedExisting] = useState<Set<string>>(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [defaultHome, setDefaultHome] = useState<string>('');
  const [existingSearch, setExistingSearch] = useState('');
  const [availableSearch, setAvailableSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isOldLayoutUnavailable, setIsOldLayoutUnavailable] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: window.innerWidth / 2 - 300, y: 100 });
  const [previewSize, setPreviewSize] = useState({ width: 600, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const previewHeaderRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  
  const theme = useTheme();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only activate drag after 8px of movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const prevExistingItemsRef = useRef<string>('');
  const prevDefaultHomeRef = useRef<string>('');
  const onUpdateRef = useRef(onUpdate);

  // Keep onUpdate ref updated
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Find default home from existing items - only set if explicitly marked as default
  useEffect(() => {
    if (existingItems.length === 0) {
      if (defaultHome) setDefaultHome('');
      return;
    }
    
    // Find item that explicitly has isDefault: true
    const defaultItem = existingItems.find(item => item.isDefault === true);
    
    if (defaultItem) {
      // If an item has isDefault: true, set defaultHome to that item's link
      if (defaultItem.link !== defaultHome) {
        setDefaultHome(defaultItem.link);
      }
    } else {
      // If no item has isDefault: true, only clear defaultHome if current defaultHome is not in the list
      // Don't automatically set to first item
      if (defaultHome && !existingItems.some(item => item.link === defaultHome)) {
        setDefaultHome('');
      }
    }
  }, [existingItems]);

  // Notify parent of changes only when values actually change
  useEffect(() => {
    const existingItemsKey = JSON.stringify(
      existingItems.map(item => ({ link: item.link, label: item.label, icon: item.icon, order: item.order }))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    );
    const hasChanged = 
      prevExistingItemsRef.current !== existingItemsKey || 
      prevDefaultHomeRef.current !== defaultHome;

    if (hasChanged) {
      // Ensure items have order and isDefault set correctly
      // isDefault should ONLY be true if item.link matches defaultHome (user-selected default)
      // If defaultHome is not set, no item should have isDefault: true
      const itemsWithDefault = existingItems.map((item, index) => ({
        ...item,
        isDefault: defaultHome ? item.link === defaultHome : false,
        order: item.order !== undefined ? item.order : index + 1,
      }));
      
      console.log('SidebarManagement - Updating items with isDefault:', itemsWithDefault.map(item => ({
        label: item.label,
        isDefault: item.isDefault,
        link: item.link,
        defaultHome
      })));
      
      prevExistingItemsRef.current = existingItemsKey;
      prevDefaultHomeRef.current = defaultHome;
      
      // Use ref to avoid dependency on onUpdate
      onUpdateRef.current(itemsWithDefault, defaultHome);
    }
  }, [existingItems, defaultHome]);

  // Fetch available items from API - only once when component mounts or oldLayout changes
  useEffect(() => {
    const fetchAvailableItems = async () => {
      if (initialAvailableItems && initialAvailableItems.length > 0) {
        setAvailableItems(initialAvailableItems);
        return; // Use provided items
      }

      setLoading(true);
      setError('');
      setIsOldLayoutUnavailable(false);
      try {
        // Only send old_layout to get all available sidebar items (not role-specific)
        const params: any = { old_layout: oldLayout };

        console.log('Fetching sidebar navigation with params:', params);
        const response = await Role.getSidebarNavigation(params);
        console.log('Sidebar navigation API response (full):', JSON.stringify(response, null, 2));
        
        // Check for error response indicating old layout is not available
        const statusCode = response?.statusCode;
        const errorCode = response?.errorCode;
        const statusText = response?.statusText;
        const hasErrorStatus = 
          (statusCode !== undefined && (String(statusCode) === '500' || statusCode === 500)) ||
          (errorCode !== undefined && (String(errorCode) === '500' || Number(errorCode) === 500)) ||
          statusText === 'fail';
        
        if (hasErrorStatus) {
          const errorMessage = String(response?.message || '');
          const isEmptyBody = !response?.body || 
            (typeof response.body === 'object' && Object.keys(response.body).length === 0);
          
          // Check if this is the old layout unavailable error
          if (isEmptyBody && (errorMessage.includes('old layout') || errorMessage.includes('Role name is required'))) {
            setIsOldLayoutUnavailable(true);
            setAvailableItems([]);
            setError('');
            setLoading(false);
            return;
          }
        }
        
        // Try multiple response structures
        let items: any[] = [];
        
        if (response?.body) {
          const body = response.body;
          console.log('Response body type:', typeof body, 'Is array:', Array.isArray(body));
          
          if (Array.isArray(body)) {
            items = body;
          } else if (body && typeof body === 'object') {
            // Check if body is empty (old layout unavailable case)
            if (Object.keys(body).length === 0) {
              setIsOldLayoutUnavailable(true);
              setAvailableItems([]);
              setError('');
              setLoading(false);
              return;
            }
            // Try nested structures
            items = body.items || body.data || body.sidebar_navigation || [];
          }
        } else if (Array.isArray(response)) {
          // Response might be directly an array
          items = response;
        }
        
        console.log('Extracted items array:', items);
        console.log('Items count:', items.length);
        
        // Ensure each item has required fields
        const validItems = items.filter(item => {
          const isValid = item && 
            typeof item.label === 'string' && 
            typeof item.link === 'string' && 
            typeof item.icon === 'string';
          if (!isValid) {
            console.warn('Invalid item filtered out:', item);
          }
          return isValid;
        });
        
          console.log('Valid items after filtering:', validItems);
          console.log('Setting availableItems to:', validItems.length, 'items');
          
          // Remove duplicates and assign order before setting
          const itemsWithOrder = assignOrder(deduplicateItems(validItems));
          setAvailableItems(itemsWithOrder);
        
        if (validItems.length === 0) {
          if (items.length > 0) {
            setError(`All ${items.length} items were filtered out due to missing required fields`);
          } else {
            setError('No sidebar items found in response');
          }
        } else {
          setError(''); // Clear error if we got valid items
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sidebar items';
        setError(errorMessage);
        console.error('Error fetching sidebar navigation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableItems();
  }, [oldLayout]);

  // Filter out items that are already in existing items
  const availableItemsFiltered = useMemo(() => {
    const filtered = availableItems.filter(
      item => !existingItems.some(existing => existing.link === item.link)
    );
    console.log('Available items:', availableItems.length);
    console.log('Existing items:', existingItems.length);
    console.log('Filtered available items:', filtered.length);
    return filtered;
  }, [availableItems, existingItems]);

  // Sort existing items by order before filtering
  const sortedExisting = useMemo(() => {
    return [...existingItems].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [existingItems]);

  const filteredExisting = sortedExisting.filter(item =>
    item.label.toLowerCase().includes(existingSearch.toLowerCase()) ||
    item.link.toLowerCase().includes(existingSearch.toLowerCase())
  );

  const filteredAvailable = availableItemsFiltered.filter(item =>
    item.label.toLowerCase().includes(availableSearch.toLowerCase()) ||
    item.link.toLowerCase().includes(availableSearch.toLowerCase())
  );

  const toggleSelection = (link: string, listType: 'existing' | 'available') => {
    console.log('Toggle selection:', link, listType);
    if (listType === 'existing') {
      setSelectedExisting(prev => {
        const newSet = new Set(prev);
        if (newSet.has(link)) {
          newSet.delete(link);
          console.log('Deselected existing item:', link, 'New set size:', newSet.size);
        } else {
          newSet.add(link);
          console.log('Selected existing item:', link, 'New set size:', newSet.size);
        }
        return newSet;
      });
    } else {
      setSelectedAvailable(prev => {
        const newSet = new Set(prev);
        if (newSet.has(link)) {
          newSet.delete(link);
          console.log('Deselected available item:', link, 'New set size:', newSet.size);
        } else {
          newSet.add(link);
          console.log('Selected available item:', link, 'New set size:', newSet.size);
        }
        return newSet;
      });
    }
  };

  const moveSelected = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      // Only move items that are in the filtered list (not already in existing)
      const itemsToMove = availableItemsFiltered.filter(item => selectedAvailable.has(item.link));
      setAvailableItems(prev => prev.filter(item => !selectedAvailable.has(item.link)));
      // Only add items that don't already exist in existingItems
      setExistingItems(prev => {
        const existingLinks = new Set(prev.map(item => item.link));
        const newItems = itemsToMove.filter(item => !existingLinks.has(item.link));
        const updated = deduplicateItems([...prev, ...newItems]);
        // Update order for all items after adding new ones
        return updateOrder(updated);
      });
      setSelectedAvailable(new Set());
    } else {
      const itemsToMove = existingItems.filter(item => selectedExisting.has(item.link));
      setExistingItems(prev => {
        const filtered = prev.filter(item => !selectedExisting.has(item.link));
        // Update order after removing items
        return updateOrder(filtered);
      });
      // Only add items that don't already exist in availableItems
      setAvailableItems(prev => {
        const existingLinks = new Set(prev.map(item => item.link));
        const newItems = itemsToMove.filter(item => !existingLinks.has(item.link));
        return deduplicateItems([...prev, ...newItems]);
      });
      setSelectedExisting(new Set());
      
      // If default home was removed, reset it
      if (itemsToMove.some(item => item.link === defaultHome)) {
        const remaining = existingItems.filter(item => !selectedExisting.has(item.link));
        if (remaining.length > 0) {
          setDefaultHome(remaining[0].link);
        } else {
          setDefaultHome('');
        }
      }
    }
  };

  const moveAll = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      // Move all filtered items (not already in existing)
      setExistingItems(prev => {
        const existingLinks = new Set(prev.map(item => item.link));
        const newItems = availableItemsFiltered.filter(item => !existingLinks.has(item.link));
        const updated = deduplicateItems([...prev, ...newItems]);
        // Update order for all items
        return updateOrder(updated);
      });
      setAvailableItems(prev => prev.filter(item => 
        !availableItemsFiltered.some(filtered => filtered.link === item.link)
      ));
      setSelectedAvailable(new Set());
    } else {
      // Move all existing items to available (avoid duplicates)
      setAvailableItems(prev => {
        const existingLinks = new Set(prev.map(item => item.link));
        const newItems = existingItems.filter(item => !existingLinks.has(item.link));
        return deduplicateItems([...prev, ...newItems]);
      });
      setExistingItems([]);
      setSelectedExisting(new Set());
      setDefaultHome('');
    }
  };

  // Handle setting default home - set clicked item as default
  // The useEffect will automatically update all items' isDefault based on defaultHome
  const handleSetDefault = useCallback((itemLink: string) => {
    setDefaultHome(itemLink);
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setExistingItems(items => {
        const oldIndex = items.findIndex(item => item.link === active.id);
        const newIndex = items.findIndex(item => item.link === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        // Update order based on new position
        return updateOrder(reordered);
      });
    }
  };

  // Drag handlers for preview modal
  const handlePreviewDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!previewHeaderRef.current) return;
    const rect = previewHeaderRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep modal within viewport
      const maxX = window.innerWidth - previewSize.width;
      const maxY = window.innerHeight - 50;
      
      setPreviewPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, previewSize]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: previewSize.width,
      height: previewSize.height,
    });
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      const newWidth = Math.max(400, Math.min(resizeStart.width + deltaX, window.innerWidth - previewPosition.x));
      const newHeight = Math.max(300, Math.min(resizeStart.height + deltaY, window.innerHeight - previewPosition.y));
      
      setPreviewSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, previewPosition]);

  return (
    <Box>
      {isOldLayoutUnavailable ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            p: 4,
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            This feature is not available right now for old layout
          </Typography>
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Sidebar Management Header with Preview Icon */}
          {/* <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Sidebar Management
            </Typography>
            <Tooltip title="Preview Sidebar">
              <IconButton
                size="small"
                onClick={() => setShowPreview(!showPreview)}
                sx={{
                  color: showPreview ? 'primary.main' : 'action.active',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <PreviewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box> */}

          {/* Sidebar Preview Modal - Portal to document.body */}
          {showPreview && existingItems.length > 0 && createPortal(
            <Box
              sx={{
                position: 'fixed',
                left: `${previewPosition.x}px`,
                top: `${previewPosition.y}px`,
                bgcolor: 'background.paper',
                boxShadow: theme.shadows[8],
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1300,
                overflow: 'visible',
                border: '1px solid',
                borderColor: theme.palette.divider,
              }}
            >
              {/* Draggable Header */}
              <Box
                ref={previewHeaderRef}
                onMouseDown={handlePreviewDragStart}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: theme.palette.divider,
                  backgroundColor: theme.palette.background.paper,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  Sidebar Preview
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowPreview(false)}
                  sx={{ color: 'text.secondary', p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Preview Content - No padding, direct sidebar */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: oldLayout ? '72px' : '256px',
                    backgroundColor: oldLayout ? '#0F284C' : theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
                    borderRight: oldLayout ? 'none' : `1px solid ${theme.palette.divider}`,
                    borderRadius: oldLayout ? 0 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: oldLayout ? 'center' : 'flex-start',
                    pt: oldLayout ? 2 : 1,
                    pb: oldLayout ? 2 : 1,
                    boxShadow: theme.shadows[4],
                    border: oldLayout ? 'none' : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {(() => {
                    // Sort sidebar navigation by order before displaying
                    // Also ensure isDefault is set correctly based on defaultHome
                    const sortedNavigation = [...existingItems].map(item => ({
                      ...item,
                      isDefault: defaultHome ? item.link === defaultHome : (item.isDefault || false),
                    })).sort((a, b) => {
                      const orderA = a.order !== undefined ? a.order : 999;
                      const orderB = b.order !== undefined ? b.order : 999;
                      return orderA - orderB;
                    });

                    return oldLayout ? (
                      // Old Layout - Blue Theme (Icon-only vertical sidebar)
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pt: 8 }}>
                        {sortedNavigation.map((item, index) => {
                          const IconComponent = getMaterialIconComponent(item.icon);
                          const isDefault = item.isDefault === true;
                          return (
                            <Tooltip key={item.link || index} title={item.label} placement="right" arrow>
                              <Box
                                sx={{
                                  width: '48px',
                                  height: '48px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 2,
                                  mb: 0.5,
                                  cursor: 'pointer',
                                  transition: 'background 0.2s',
                                  backgroundColor: isDefault ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  },
                                }}
                              >
                                {IconComponent && React.cloneElement(IconComponent, {
                                  sx: { fontSize: 22, color: '#fff' }
                                })}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    ) : (
                      // New Layout - Black/White Theme (Full sidebar with labels)
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, px: 1.5, pt: 2 }}>
                        {sortedNavigation.map((item, index) => {
                          const IconComponent = getMaterialIconComponent(item.icon);
                          const isDefault = item.isDefault === true;
                          return (
                            <Box
                              key={item.link || index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 1.5,
                                py: 1.2,
                                borderRadius: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                backgroundColor: isDefault ? '#000000' : 'transparent',
                                color: isDefault
                                  ? '#ffffff'
                                  : theme.palette.text.primary,
                                '&:hover': {
                                  backgroundColor: isDefault
                                    ? '#1a1a1a'
                                    : theme.palette.action.hover,
                                },
                              }}
                            >
                              {IconComponent && React.cloneElement(IconComponent, {
                                sx: { fontSize: 20, color: isDefault ? '#ffffff' : 'inherit' }
                              })}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

              {/* Resize Handle */}
              <Box
                ref={resizeHandleRef}
                onMouseDown={handleResizeStart}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 20,
                  height: 20,
                  cursor: 'nwse-resize',
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    borderWidth: '0 0 12px 12px',
                    borderColor: `transparent transparent ${theme.palette.text.secondary} transparent`,
                  },
                }}
              />
            </Box>,
            document.body
          )}

          <Box display="flex" gap={4} p={2}>
        {/* Available Sidebar Items (Left) */}
        <Paper sx={{ width: '100%', flex: 1 }}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">
                Available Items
              </Typography>
              <Chip
                label={`${selectedAvailable.size} of ${availableItemsFiltered.length} selected`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search available items..."
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
              sx={{ mt: 2, mb: 1 }}
              variant="outlined"
            />
          </Box>
          <Divider />
          <List sx={{ height: 400, overflow: 'auto', p: 0.5 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={24} />
              </Box>
            ) : filteredAvailable.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.5,
                  fontStyle: 'italic',
                  gap: 1,
                }}
              >
                <Typography>No items available</Typography>
                {availableItems.length > 0 && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    ({availableItems.length} items from API, but all are already in sidebar)
                  </Typography>
                )}
                {error && (
                  <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                    Error: {error}
                  </Typography>
                )}
              </Box>
            ) : (
              filteredAvailable.map((item) => {
                const IconComponent = getMaterialIconComponent(item.icon);
                return (
                  <ListItem
                    key={item.link}
                    sx={{
                      mb: 0.25,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: selectedAvailable.has(item.link)
                        ? 'rgba(255, 165, 0, 0.2)'
                        : 'background.paper',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedAvailable.has(item.link)
                          ? 'rgba(255, 165, 0, 0.3)'
                          : 'action.hover',
                      },
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelection(item.link, 'available');
                    }}
                  >
                    {IconComponent && (
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {React.cloneElement(IconComponent, { sx: { fontSize: 18 } })}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.label}
                      secondary={item.link}
                      primaryTypographyProps={{
                        fontWeight: selectedAvailable.has(item.link) ? 'bold' : 'normal',
                        fontSize: '0.875rem',
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                      }}
                      sx={{ my: 0 }}
                    />
                  </ListItem>
                );
              })
            )}
          </List>
        </Paper>

        {/* Transfer Controls */}
        <Box display="flex" flexDirection="column" justifyContent="center" gap={2}>
          <Tooltip title="Move all to sidebar">
            <span>
              <IconButton
                onClick={() => moveAll('right')}
                disabled={availableItemsFiltered.length === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardDoubleArrowRight fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Move selected to sidebar">
            <span>
              <IconButton
                onClick={() => moveSelected('right')}
                disabled={selectedAvailable.size === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardArrowRight fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={`Move selected to available (${selectedExisting.size} selected)`}>
            <span>
              <IconButton
                onClick={() => {
                  console.log('Moving selected items left. Selected:', Array.from(selectedExisting));
                  moveSelected('left');
                }}
                disabled={selectedExisting.size === 0}
                size="large"
                color={selectedExisting.size > 0 ? "primary" : "default"}
                sx={{ 
                  border: '1px solid', 
                  borderColor: selectedExisting.size > 0 ? 'primary.main' : 'divider',
                  opacity: selectedExisting.size === 0 ? 0.4 : 1,
                  cursor: selectedExisting.size === 0 ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: selectedExisting.size > 0 ? 'primary.light' : 'action.hover',
                  },
                }}
              >
                <KeyboardArrowLeft fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Move all to available">
            <span>
              <IconButton
                onClick={() => moveAll('left')}
                disabled={existingItems.length === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardDoubleArrowLeft fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Current Sidebar Items (Right) */}
        <Paper sx={{ width: '100%', flex: 1 }}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">
                Current Sidebar Items
              </Typography>
              <Chip
                label={`${selectedExisting.size} of ${existingItems.length} selected`}
                size="small"
                color={selectedExisting.size > 0 ? "primary" : "default"}
                variant={selectedExisting.size > 0 ? "filled" : "outlined"}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search current items..."
              value={existingSearch}
              onChange={(e) => setExistingSearch(e.target.value)}
              sx={{ mt: 2, mb: 1 }}
              variant="outlined"
            />
          </Box>
          <Divider />
          <Box sx={{ height: 400, overflow: 'auto', p: 0.5 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={24} />
              </Box>
            ) : filteredExisting.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.5,
                  fontStyle: 'italic',
                }}
              >
                No items in sidebar
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredExisting.map(item => item.link)}
                  strategy={verticalListSortingStrategy}
                >
                  <List>
                    {filteredExisting.map((item) => (
                      <SortableItem
                        key={item.link}
                        item={item}
                        isSelected={selectedExisting.has(item.link)}
                        onToggle={() => toggleSelection(item.link, 'existing')}
                        isDefault={item.link === defaultHome}
                        onSetDefault={() => handleSetDefault(item.link)}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            )}
          </Box>
        </Paper>
      </Box>
        </>
      )}
    </Box>
  );
};

export default SidebarManagement;
